<template>
  <div class="message-input" :class="{ 'pressing-mode': isPressing && inputMode === 'voice' }">
    <!-- 灰色背景遮罩（按住时显示） -->
    <div v-if="isPressing && inputMode === 'voice'" class="recording-overlay"></div>

    <div class="input-container" :class="{
      recording: isRecording && inputMode === 'voice',
      'cancel-mode': isCancelMode && inputMode === 'voice',
      pressing: (isPressing || isTouching) && inputMode === 'voice'
    }">
      <!-- 左侧图标按钮 -->
      <button class="mode-switch-btn" @click="inputMode === 'voice' ? switchToTextMode() : switchToVoiceMode()"
        :title="inputMode === 'voice' ? '切换到文字输入' : '切换到语音输入'">
        <!-- 文字模式：显示麦克风图标（点击切到语音输入） -->
        <svg v-if="inputMode === 'text'" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
          <path
            d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
        </svg>
        <!-- 语音模式：显示键盘图标（点击切到文字输入） -->
        <svg v-if="inputMode === 'voice'" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path
            d="M20 5H4c-1.1 0-1.99.9-1.99 2L2 17c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-9 3h2v2h-2V8zm0 3h2v2h-2v-2zM8 8h2v2H8V8zm0 3h2v2H8v-2zm-1 2H5v-2h2v2zm0-3H5V8h2v2zm9 7H8v-2h8v2zm0-4h-2v-2h2v2zm0-3h-2V8h2v2zm3 3h-2v-2h2v2zm0-3h-2V8h2v2z" />
        </svg>
      </button>

      <!-- 中间内容区域 -->
      <!-- 语音模式：按住说话 -->
      <button v-if="inputMode === 'voice'" ref="voiceRecordBtn" class="voice-record-btn" @contextmenu.prevent
        @touchstart="handleTouchStart" @touchend="handleTouchEnd" @touchcancel="handleTouchEnd"
        @mousedown="handleTouchStart" @mouseup="handleTouchEnd" @mouseleave="handleTouchEnd">
        <span class="btn-text">{{ buttonText }}</span>
      </button>

      <!-- 文字模式：输入框 -->
      <div v-else class="input-content">
        <textarea v-model="inputText" placeholder="请输入您的回答..." class="text-input" rows="1" maxlength="500"
          @keydown="handleKeydown" @input="adjustTextareaHeight" ref="textareaRef" />
      </div>

      <!-- 右侧发送按钮（仅文字模式显示） -->
      <button v-if="inputMode === 'text' && canSend" class="send-btn" @click="sendTextMessage">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
        </svg>
      </button>

    </div>

    <!-- 录音状态提示 -->
    <div v-if="isRecording" class="recording-indicator">
      <div class="recording-animation">
        <div class="wave"></div>
        <div class="wave"></div>
        <div class="wave"></div>
      </div>
      <div class="recording-info">
        <span class="recording-text">正在录音中...</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onUnmounted } from 'vue'
import { showToast } from 'vant'
import { useChatStore } from '@/stores/chat.js'
import { useAppStore } from '@/stores/app.js'
import { resumeAudioContext } from '@/composables/useAudio.js'
import {
  HAMMER_PAN_CONFIG,
  HAMMER_PRESS_CONFIG,
  RECORDING_CANCEL_CONFIG,
  RECORDING_DURATION_CONFIG,
  HAMMER_INIT_DELAY
} from '@/constants/hammer.js'
import Hammer from 'hammerjs'

const chatStore = useChatStore()
const appStore = useAppStore()

// ==================== 状态 ====================

const inputText = ref('')
const inputMode = ref('text') // 'text' | 'voice' - 默认文字输入，用户可手动切到语音
const textareaRef = ref()
const voiceRecordBtn = ref()

// 录音交互状态
const isCancelMode = ref(false) // 是否处于取消模式（手指移出按钮范围）
const recordingStartTime = ref(null) // 录音开始时间（本地记录）
const isPressing = ref(false) // 是否正在长按（逻辑状态）
const isTouching = ref(false) // 是否正在触摸（视觉状态，即时响应）

// 直接使用chatStore的状态（不再维护本地状态）
const isRecording = computed(() => chatStore.isRecording)

/**
 * 按钮文字
 */
const buttonText = computed(() => {
  if (isCancelMode.value) {
    return '取消录音'
  }
  if (isPressing.value || isRecording.value || isTouching.value) {
    return '松开发送'
  }
  return '按住说话'
})

// ==================== 计算属性 ====================

/**
 * 是否可以发送文字消息
 */
const canSend = computed(() => {
  // 文字模式下，只要有内容就可以发送
  return inputMode.value === 'text' && inputText.value.trim().length > 0
})

// ==================== 文字输入 ====================

/**
 * 自动调整输入框高度
 */
const adjustTextareaHeight = async () => {
  await nextTick()
  if (textareaRef.value) {
    textareaRef.value.style.height = 'auto'
    textareaRef.value.style.height = Math.min(textareaRef.value.scrollHeight, 120) + 'px'
  }
}

/**
 * 处理键盘事件
 * @param {KeyboardEvent} event - 键盘事件
 * @returns {void}
 */
const handleKeydown = (event) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    sendTextMessage()
  }
}

/**
 * 发送文字消息
 * @returns {Promise<void>}
 */
const sendTextMessage = async () => {
  if (!canSend.value) return

  const text = inputText.value.trim()
  inputText.value = ''

  // 重置输入框高度
  if (textareaRef.value) {
    textareaRef.value.style.height = 'auto'
  }

  // 标记用户已交互
  appStore.markUserInteraction()

  // 尝试恢复音频上下文（微信环境）
  try {
    await resumeAudioContext()
  } catch (error) {
    // warn('恢复音频上下文失败:', error)
  }

  try {
    await chatStore.sendTextMessage(text)
  } catch (error) {
    // error('发送文字消息失败:', error)
    // 发送失败时恢复输入内容
    inputText.value = text
    showToast({
      type: 'fail',
      message: '发送失败，请重试',
      duration: 2000
    })
  }
}

// ==================== 语音输入（Hammer.js）====================

let hammerInstance = null

/**
 * 检测触摸点是否在按钮范围内
 * @param {TouchEvent|HammerInput} event - 触摸事件或Hammer事件
 * @returns {boolean} 是否在按钮范围内
 */
const isPointInButton = (event) => {
  if (!voiceRecordBtn.value) return false

  const rect = voiceRecordBtn.value.getBoundingClientRect()
  let clientX, clientY

  // 处理Hammer事件
  if (event.center) {
    clientX = event.center.x
    clientY = event.center.y
  }
  // 处理原生Touch事件
  else if (event.touches && event.touches.length > 0) {
    clientX = event.touches[0].clientX
    clientY = event.touches[0].clientY
  }
  // 处理Mouse事件
  else if (event.clientX !== undefined) {
    clientX = event.clientX
    clientY = event.clientY
  } else {
    return false
  }

  return (
    clientX >= rect.left &&
    clientX <= rect.right &&
    clientY >= rect.top &&
    clientY <= rect.bottom
  )
}

/**
 * 初始化 Hammer.js 手势识别
 * @returns {void}
 */
const initializeHammer = () => {
  if (!voiceRecordBtn.value) {
    // warn('录音按钮未找到，跳过初始化')
    return
  }

  if (hammerInstance) {
    destroyHammer()
  }

  // debug('🔧 初始化Hammer.js')

  try {
    hammerInstance = new Hammer(voiceRecordBtn.value, {
      // 阻止所有手势事件的默认行为
      preventDefault: true
    })

    // 配置手势识别
    hammerInstance.get('pan').set({
      direction: Hammer.DIRECTION_ALL, // 允许所有方向的滑动
      threshold: HAMMER_PAN_CONFIG.THRESHOLD
    })
    hammerInstance.get('press').set({
      time: HAMMER_PRESS_CONFIG.TIME,
      threshold: HAMMER_PRESS_CONFIG.THRESHOLD
    })

    // 长按开始录音
    hammerInstance.on('press', async (event) => {
      // debug('🎤 长按事件触发')

      // 阻止默认行为（防止文本选择、复制等）
      event.preventDefault()
      if (event.srcEvent) {
        event.srcEvent.preventDefault()
        event.srcEvent.stopPropagation()
      }

      // 授权守卫：微信 SDK 未就绪时，不推进任何视觉/逻辑状态，
      // 避免 UI 出现"按住→松开发送"的假录音态（底层 wx.startRecord 实际未授权）
      if (!appStore.wechatReady) {
        showToast({
          type: 'fail',
          message: '录音权限准备中，请刷新页面后重试',
          duration: 2500
        })
        return
      }

      isPressing.value = true // 标记正在长按
      isCancelMode.value = false
      recordingStartTime.value = Date.now()

      try {
        await startRecording()
      } catch (error) {
        // error('❌ 开始录音失败:', error)
        recordingStartTime.value = null
        isPressing.value = false // 重置标志
        showToast({ type: 'fail', message: error.message || '录音启动失败' })
      }
    })

    // 滑动时检测手指位置
    hammerInstance.on('pan', (event) => {
      if (!chatStore.isRecording) return

      // 阻止默认行为（防止页面滚动等）
      event.preventDefault()
      if (event.srcEvent) {
        event.srcEvent.preventDefault()
        event.srcEvent.stopPropagation()
      }

      const inButton = isPointInButton(event)

      if (inButton !== !isCancelMode.value) {
        isCancelMode.value = !inButton
        // debug('🎤 手指位置变化:', { inButton, isCancelMode: isCancelMode.value })
      }
    })

    // 松开结束
    hammerInstance.on('panend pressup', async (event) => {
      // debug('🎤 松开事件触发，录音状态:', chatStore.isRecording)

      // 阻止默认行为
      event.preventDefault()
      if (event.srcEvent) {
        event.srcEvent.preventDefault()
        event.srcEvent.stopPropagation()
      }

      if (!chatStore.isRecording) {
        // debug('🎤 没有录音在进行')
        isCancelMode.value = false
        isPressing.value = false // 重置标志
        return
      }

      // 检测松开时的位置（只根据松开时的位置判断）
      const inButton = isPointInButton(event)
      const shouldCancel = !inButton

      // 检查录音时长
      const recordingDuration = recordingStartTime.value
        ? Date.now() - recordingStartTime.value
        : 0

      // 录音结束判断

      // 立即重置UI状态（不等待接口请求完成）
      isCancelMode.value = false
      isPressing.value = false
      const savedRecordingStartTime = recordingStartTime.value
      recordingStartTime.value = null

      try {
        if (shouldCancel) {
          // 取消录音
          // debug('📝 取消录音')
          cancelRecording()
          showToast({ type: 'info', message: '录音已取消', duration: 1500 })
        } else if (recordingDuration < RECORDING_DURATION_CONFIG.MIN_DURATION) {
          // 录音时长太短，取消
          // debug('📝 录音时长太短，取消录音')
          cancelRecording()
          showToast({ type: 'info', message: '录音时间太短', duration: 1500 })
        } else {
          // 发送录音
          // debug('📝 完成录音，准备发送')
          await stopRecording()
          showToast({ message: '录音发送成功', duration: 1500 })
        }
      } catch (error) {
        // error('❌ 结束录音失败:', error)
      }
    })

    // info('✅ Hammer.js初始化完成')
  } catch (error) {
    // error('❌ Hammer.js初始化失败:', error)
  }
}

/**
 * 销毁 Hammer.js
 * @returns {void}
 */
const destroyHammer = () => {
  if (hammerInstance) {
    // debug('🔧 销毁Hammer.js')
    hammerInstance.destroy()
    hammerInstance = null
  }
}

// ==================== 录音控制（直接调用chatStore）====================

/**
 * 开始录音
 * @returns {Promise<void>}
 * @throws {Error} 录音失败
 */
const startRecording = async () => {
  // debug('🎤 开始录音')

  // 标记用户已交互
  appStore.markUserInteraction()

  // 尝试恢复音频上下文
  try {
    await resumeAudioContext()
  } catch (error) {
    // warn('恢复音频上下文失败:', error)
  }

  // 直接调用chatStore方法
  await chatStore.startRecording()
}

/**
 * 停止录音
 * @returns {Promise<void>}
 * @throws {Error} 停止录音失败
 */
const stopRecording = async () => {
  // debug('🎤 停止录音')

  // 直接调用chatStore方法
  await chatStore.stopRecording()
}

/**
 * 取消录音
 * @returns {void}
 */
const cancelRecording = () => {
  // debug('🎤 取消录音')

  // 直接调用chatStore方法（状态已在调用前统一重置）
  chatStore.cancelRecording()
}

// ==================== 模式切换 ====================

/**
 * 切换到语音模式
 * @returns {void}
 */
const switchToVoiceMode = () => {
  // 切换前重置语音交互态，避免残留视觉状态（图标/按钮文字错乱）
  isPressing.value = false
  isTouching.value = false
  isCancelMode.value = false
  inputMode.value = 'voice'
  nextTick(() => {
    setTimeout(() => initializeHammer(), HAMMER_INIT_DELAY)
  })
}

/**
 * 切换到文字模式
 * @returns {void}
 */
const switchToTextMode = () => {
  // 切换前重置语音交互态，避免切到文字后仍显示"松开发送"等残留状态
  isPressing.value = false
  isTouching.value = false
  isCancelMode.value = false
  inputMode.value = 'text'
  destroyHammer()
}

/**
 * 清空输入框
 * @returns {void}
 */
const clearInput = () => {
  inputText.value = ''
  if (textareaRef.value) {
    textareaRef.value.style.height = 'auto'
  }
}

// ==================== 视觉反馈处理 ====================

/**
 * 处理触摸开始（即时视觉反馈）
 */
const handleTouchStart = () => {
  if (inputMode.value !== 'voice') return
  isTouching.value = true
}

/**
 * 处理触摸结束
 */
const handleTouchEnd = () => {
  // 延迟一小段时间重置，避免闪烁
  setTimeout(() => {
    isTouching.value = false
  }, 50)
}

// ==================== 生命周期 ====================

onMounted(() => {
  // 只在语音模式下初始化 Hammer
  if (inputMode.value === 'voice') {
    nextTick(() => {
      setTimeout(() => initializeHammer(), HAMMER_INIT_DELAY)
    })
  }
})

onUnmounted(() => {
  // 如果正在录音，取消录音
  if (chatStore.isRecording) {
    chatStore.cancelRecording()
  }
  destroyHammer()
})

defineExpose({
  clearInput,
  switchToVoiceMode,
  switchToTextMode
})
</script>

<style lang="scss" scoped>
.message-input {
  position: fixed;
  bottom: calc(10px + var(--kb-offset, 0px));
  left: 0;
  right: 0;
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
  z-index: 1000;
  transition: bottom 0.2s ease-out, padding 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  // 按住时，父容器向下移动并调整内边距
  &.pressing-mode {
    bottom: 0;
    padding: 0 0 20px 0; // 底部保留一点距离，不要完全贴底
  }
}

// 灰色背景遮罩（按住时显示）
.recording-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 999;
  pointer-events: none;
  animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 60px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border: 2px solid transparent;
  border-radius: 30px;
  padding: 0 8px;
  transition: width 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    height 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    border-radius 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94),
    background 0.15s linear,
    box-shadow 0.2s ease;
  box-sizing: border-box;
  margin: 0 auto;
  z-index: 1001; // 确保在遮罩层之上

  // 按住时的样式（类似微信）
  &.pressing {
    width: 100%;
    height: 120px; // 稍微增高一点
    margin: 0;
    border-top-left-radius: 50%; // 恢复之前的曲线效果
    border-top-right-radius: 50%;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border: none; // 移除边框
    background: #f3f4f6; // 使用浅灰色背景，而不是渐变蓝，更柔和
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
    transform: translateY(0);
  }

  &.recording {
    background: #f3f4f6;
    border: none;
  }

  &.cancel-mode {
  &.cancel-mode {
    background: #fee2e2; // 浅红色背景
    border: none;
  }
  }


}

// 模式切换按钮（左侧图标）
.mode-switch-btn {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  background: transparent;
  border: none;
  color: #8b5cf6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;

  &:hover {
    background: #f3f4f6;
    color: #7c3aed;
  }

  &:active {
    background: #e5e7eb;
    transform: scale(0.95);
  }

  svg {
    width: 24px;
    height: 24px;
  }
}

// 中间内容区域（文字模式）
.input-content {
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 26px;
}

// 文字输入框
.text-input {
  width: 100%;
  border: none;
  outline: none;
  padding: 0;
  font-size: 18px;
  line-height: 1.6;
  resize: none;
  background: transparent;
  min-height: 24px;
  max-height: 120px;
  caret-color: #1989fa;
  overflow: hidden;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &::placeholder {
    color: #999;
    font-size: 18px;
  }
}

// 发送按钮
.send-btn {
  flex-shrink: 0;
  min-width: 40px;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  color: #8b5cf6;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    color: #7c3aed;
  }

  &:active {
    color: #6d28d9;
    transform: scale(0.95);
  }
}

// 语音录音按钮（中间内容区域）
.voice-record-btn {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  border-radius: 0;
  background: transparent;
  border: none;
  color: #333;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  touch-action: none;
  /* 确保触摸事件正常工作 */

  .btn-text {
    background: linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    text-align: center;
    pointer-events: none;
    /* 确保文字区域不阻止事件 */
    font-size: 16px;
  }

  &:active {
    transform: scale(0.98);
  }
}

// 按住和录音状态下的样式（通过父容器控制）
.input-container.pressing,
.input-container.recording {
  .voice-record-btn {
    .btn-text {
      background: none;
      -webkit-text-fill-color: #3b82f6; // 文字变蓝
      color: #3b82f6;
      font-size: 18px;
      font-weight: 600;
    }
  }
}

// 取消模式下的样式（通过父容器控制）
.input-container.cancel-mode {
  .voice-record-btn {
    .btn-text {
      background: none;
      -webkit-text-fill-color: #ef4444; // 文字变红
      color: #ef4444;
      font-size: 18px;
      font-weight: 600;
    }
  }
}

// 录音状态提示
.recording-indicator {
  position: absolute;
  top: -100px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 24px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  color: white;
  /* backdrop-filter: blur(10px); 移除模糊以便能看清下面的字 */
  min-width: 200px;
}

.recording-animation {
  display: flex;
  gap: 3px;

  .wave {
    width: 3px;
    height: 20px;
    background: white;
    border-radius: 2px;
    animation: wave 1.2s ease-in-out infinite;

    &:nth-child(2) {
      animation-delay: 0.1s;
    }

    &:nth-child(3) {
      animation-delay: 0.2s;
    }
  }
}

.recording-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  .recording-text {
    font-size: 14px;
    color: white;
    font-weight: 500;
  }
}

@keyframes wave {

  0%,
  40%,
  100% {
    transform: scaleY(0.4);
  }

  20% {
    transform: scaleY(1);
  }
}

@keyframes pulse {

  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.1);
  }
}

// 移动端适配
@media (max-width: 480px) {
  .message-input {
    padding: 8px;
  }

  .input-container {
    gap: 8px;
    height: 60px;
  }

  .voice-btn,
  .keyboard-btn {
    width: 44px;
    height: 44px;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .send-btn {
    min-width: 64px;
    height: 44px;
    font-size: 16px;
  }

  .voice-record-btn {
    font-size: 17px;
  }

  .input-wrapper {
    min-height: 44px;
  }

  .text-input {
    padding: 10px 14px;
    font-size: 17px;
  }
}

// 安全区域适配
@supports (padding: max(0px)) {
  .message-input {
    bottom: calc(max(10px, 10px + env(safe-area-inset-bottom)) + var(--kb-offset, 0px));
    padding-bottom: 8px;
  }
}
</style>
